import { Component, OnInit, inject, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SuppliersService } from '../../core/services/suppliers.service';
import { PayablesService } from '../../core/services/payables.service';
import { PaymentsService } from '../../core/services/payments.service';
import { PaymentDetailsDialogComponent } from '../payables/payables.component';
import { SupplierDetail, SupplierPayableDocument } from '../../core/models/supplier.model';
import { PayableDocument, PaymentStatus, DocumentType, CreatePayableDto } from '../../core/models/payable.model';
import { Payment, CreatePaymentDto, ManualAllocation } from '../../core/models/payment.model';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';

// Timeline item interface for combined view
interface TimelineItem {
  type: 'payment' | 'payable';
  date: Date;
  data: Payment | PayableDocument;
}

@Component({
  selector: 'app-supplier-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTabsModule,
    DecimalPipe,
    DatePipe
  ],
  template: `
    <div class="page-header">
      <div class="header-left">
        <button mat-icon-button (click)="goBack()" class="back-button" matTooltip="Nazad na listu">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <div>
          <h1 class="page-title">{{ supplier?.name || 'Učitavanje...' }}</h1>
          <p class="page-subtitle">Pregled transakcija i stanja</p>
        </div>
      </div>
      <div class="header-actions" *ngIf="supplier">
        <button mat-raised-button color="primary" (click)="openAddPayable()">
          <mat-icon>add</mat-icon>
          Novo dugovanje
        </button>
        <button mat-raised-button color="accent" class="payment-btn" (click)="openAddPayment()">
          <mat-icon>payments</mat-icon>
          Nova uplata
        </button>
      </div>
    </div>

    <div class="loading-container" *ngIf="loading">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <div class="content" *ngIf="!loading && supplier">
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="card-icon blue">
            <mat-icon>receipt_long</mat-icon>
          </div>
          <div class="card-content">
            <div class="card-value">{{ supplier.totalAmount | number:'1.2-2' }} RSD</div>
            <div class="card-label">Ukupna dugovanja</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="card-icon green">
            <mat-icon>payments</mat-icon>
          </div>
          <div class="card-content">
            <div class="card-value">{{ supplier.totalPaid | number:'1.2-2' }} RSD</div>
            <div class="card-label">Ukupno plaćeno</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="card-icon" [ngClass]="supplier.totalRemaining > 0 ? 'orange' : 'green'">
            <mat-icon>{{ supplier.totalRemaining > 0 ? 'pending_actions' : 'check_circle' }}</mat-icon>
          </div>
          <div class="card-content">
            <div class="card-value">{{ supplier.totalRemaining | number:'1.2-2' }} RSD</div>
            <div class="card-label">Preostalo za plaćanje</div>
          </div>
        </div>
        <div class="summary-card" *ngIf="supplier.overdueCount > 0">
          <div class="card-icon red">
            <mat-icon>warning</mat-icon>
          </div>
          <div class="card-content">
            <div class="card-value">{{ supplier.overdueCount }}</div>
            <div class="card-label">Dospelih računa</div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-container">
        <div class="filters-row">
          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Tip</mat-label>
            <mat-select [(ngModel)]="filterType" (selectionChange)="applyFilters()">
              <mat-option value="all">Sve transakcije</mat-option>
              <mat-option value="payable">Samo dugovanja</mat-option>
              <mat-option value="payment">Samo uplate</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Od datuma</mat-label>
            <input matInput [matDatepicker]="fromPicker" [(ngModel)]="filterFromDate" (dateChange)="applyFilters()">
            <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
            <mat-datepicker #fromPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Do datuma</mat-label>
            <input matInput [matDatepicker]="toPicker" [(ngModel)]="filterToDate" (dateChange)="applyFilters()">
            <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
            <mat-datepicker #toPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field" *ngIf="filterType !== 'payment'">
            <mat-label>Status</mat-label>
            <mat-select [(ngModel)]="filterStatus" (selectionChange)="applyFilters()">
              <mat-option [value]="null">Svi statusi</mat-option>
              <mat-option [value]="0">Neplaćeno</mat-option>
              <mat-option [value]="1">Delimično plaćeno</mat-option>
              <mat-option [value]="2">Plaćeno</mat-option>
            </mat-select>
          </mat-form-field>

          <button mat-stroked-button (click)="clearFilters()" *ngIf="hasFilters()">
            <mat-icon>clear</mat-icon>
            Očisti
          </button>
        </div>
      </div>

      <!-- Timeline Table -->
      <div class="timeline-section">
        <div class="timeline-header">
          <h2 class="section-title">
            <mat-icon>history</mat-icon>
            Hronologija ({{ filteredTimeline.length }})
          </h2>
        </div>

        <div class="table-container" *ngIf="filteredTimeline.length > 0">
          <table mat-table [dataSource]="filteredTimeline" class="timeline-table">
            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef>Tip</th>
              <td mat-cell *matCellDef="let item">
                <span class="type-badge" [ngClass]="item.type">
                  {{ item.type === 'payment' ? 'UPLATA' : getDocumentTypeLabel(asPayable(item.data).documentType) }}
                </span>
              </td>
            </ng-container>
            <ng-container matColumnDef="number">
              <th mat-header-cell *matHeaderCellDef>Broj</th>
              <td mat-cell *matCellDef="let item">
                <span *ngIf="item.type === 'payable'">{{ asPayable(item.data).invoiceNumber || '-' }}</span>
                <span *ngIf="item.type === 'payment'" class="ref-number">{{ asPayment(item.data).referenceNumber || '-' }}</span>
              </td>
            </ng-container>
            <ng-container matColumnDef="date">
              <th mat-header-cell *matHeaderCellDef>Datum</th>
              <td mat-cell *matCellDef="let item">{{ item.date | date:'dd.MM.yyyy' }}</td>
            </ng-container>
            <ng-container matColumnDef="amount">
              <th mat-header-cell *matHeaderCellDef class="text-right">Iznos</th>
              <td mat-cell *matCellDef="let item" class="text-right">
                <span [ngClass]="{'payment-amount': item.type === 'payment', 'payable-amount': item.type === 'payable'}">
                  {{ item.type === 'payment' ? '+' : '-' }} {{ (item.type === 'payment' ? asPayment(item.data).amount : asPayable(item.data).amount) | number:'1.2-2' }}
                </span>
              </td>
            </ng-container>
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef class="text-center">Status</th>
              <td mat-cell *matCellDef="let item" class="text-center">
                <ng-container *ngIf="item.type === 'payable'">
                  <span class="status-badge" [ngClass]="getPayableStatusClass(asPayable(item.data))">
                    {{ getPayableStatusLabel(asPayable(item.data)) }}
                  </span>
                  <span class="overdue-badge" *ngIf="asPayable(item.data).isOverdue && asPayable(item.data).remainingAmount > 0">!</span>
                </ng-container>
                <ng-container *ngIf="item.type === 'payment'">
                  <span class="status-badge allocated" *ngIf="asPayment(item.data).unallocatedAmount === 0">Raspoređeno</span>
                  <span class="status-badge partial" *ngIf="asPayment(item.data).unallocatedAmount > 0">
                    Nerasp: {{ asPayment(item.data).unallocatedAmount | number:'1.0-0' }}
                  </span>
                </ng-container>
              </td>
            </ng-container>
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="actions-col"></th>
              <td mat-cell *matCellDef="let item" class="actions-col">
                <button mat-icon-button *ngIf="item.type === 'payable' && asPayable(item.data).paidAmount > 0"
                        (click)="viewPayablePayments(asPayable(item.data))" matTooltip="Pregled uplata" class="small-btn">
                  <mat-icon>receipt_long</mat-icon>
                </button>
                <button mat-icon-button *ngIf="item.type === 'payable'" (click)="editPayable(asPayable(item.data))" matTooltip="Izmeni" class="small-btn">
                  <mat-icon>edit</mat-icon>
                </button>
                <button mat-icon-button (click)="deleteItem(item)" matTooltip="Obriši" class="small-btn delete-btn">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="timelineCols"></tr>
            <tr mat-row *matRowDef="let row; columns: timelineCols;"
                [ngClass]="{'payment-row': row.type === 'payment', 'payable-row': row.type === 'payable'}"></tr>
          </table>
        </div>

        <div class="empty-state" *ngIf="filteredTimeline.length === 0">
          <mat-icon>history</mat-icon>
          <p>Nema transakcija za prikaz</p>
          <div class="empty-actions">
            <button mat-stroked-button color="primary" (click)="openAddPayable()">
              <mat-icon>add</mat-icon>
              Dodaj dugovanje
            </button>
            <button mat-stroked-button color="accent" (click)="openAddPayment()">
              <mat-icon>payments</mat-icon>
              Dodaj uplatu
            </button>
          </div>
        </div>
      </div>

      <!-- Supplier Info (collapsed) -->
      <div class="info-section" *ngIf="hasBasicInfo()">
        <mat-card class="info-card">
          <mat-card-header (click)="infoExpanded = !infoExpanded" class="clickable-header">
            <mat-card-title>
              <mat-icon class="section-icon">business</mat-icon>
              Podaci o dobavljaču
              <mat-icon class="expand-icon">{{ infoExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
            </mat-card-title>
          </mat-card-header>
          <mat-card-content *ngIf="infoExpanded">
            <div class="info-grid">
              <div class="info-item" *ngIf="supplier.address || supplier.city">
                <span class="info-label">Adresa</span>
                <span class="info-value">{{ supplier.address }} {{ supplier.city }} {{ supplier.postalCode }}</span>
              </div>
              <div class="info-item" *ngIf="supplier.taxId">
                <span class="info-label">PIB</span>
                <span class="info-value">{{ supplier.taxId }}</span>
              </div>
              <div class="info-item" *ngIf="supplier.registrationNumber">
                <span class="info-label">Matični broj</span>
                <span class="info-value">{{ supplier.registrationNumber }}</span>
              </div>
              <div class="info-item" *ngIf="supplier.phone">
                <span class="info-label">Telefon</span>
                <span class="info-value">{{ supplier.phone }}</span>
              </div>
              <div class="info-item" *ngIf="supplier.email">
                <span class="info-label">Email</span>
                <span class="info-value">{{ supplier.email }}</span>
              </div>
              <div class="info-item" *ngIf="supplier.contactPerson">
                <span class="info-label">Kontakt</span>
                <span class="info-value">{{ supplier.contactPerson }}</span>
              </div>
            </div>
            <div class="bank-accounts" *ngIf="supplier.bankAccounts?.length > 0">
              <h4>Žiro računi</h4>
              <div class="bank-account" *ngFor="let acc of supplier.bankAccounts">
                <span class="account-number">{{ acc.accountNumber }}</span>
                <span class="primary-badge" *ngIf="acc.isPrimary">Primarni</span>
                <span class="bank-name" *ngIf="acc.bankName">{{ acc.bankName }}</span>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>

    <div class="error-state" *ngIf="!loading && !supplier">
      <mat-icon>error_outline</mat-icon>
      <p>Dobavljač nije pronađen</p>
      <button mat-stroked-button (click)="goBack()">Nazad na listu</button>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .back-button {
      color: #6B7280;
    }

    .page-title {
      margin: 0;
      font-size: 28px;
      font-weight: 600;
      color: #1F2937;
    }

    .page-subtitle {
      margin: 4px 0 0 0;
      color: #6B7280;
      font-size: 14px;
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }

    .payment-btn {
      background-color: #10B981 !important;
      color: white !important;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      padding: 60px;
    }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 16px;
      margin-bottom: 24px;
    }

    .summary-card {
      background: white;
      border-radius: 8px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .card-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .card-icon mat-icon {
      color: white;
      font-size: 24px;
      width: 24px;
      height: 24px;
    }

    .card-icon.blue { background: linear-gradient(135deg, #3B82F6, #2563EB); }
    .card-icon.green { background: linear-gradient(135deg, #10B981, #059669); }
    .card-icon.orange { background: linear-gradient(135deg, #F59E0B, #D97706); }
    .card-icon.red { background: linear-gradient(135deg, #EF4444, #DC2626); }

    .card-value {
      font-size: 20px;
      font-weight: 700;
      color: #1F2937;
    }

    .card-label {
      font-size: 13px;
      color: #6B7280;
    }

    .filters-container {
      background: white;
      border-radius: 8px;
      padding: 16px 20px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .filters-row {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .filter-field {
      width: 180px;
    }

    .timeline-section {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
      margin-bottom: 24px;
      overflow: hidden;
    }

    .timeline-header {
      padding: 12px 20px;
      border-bottom: 1px solid #E5E7EB;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #374151;
    }

    .section-title mat-icon {
      color: #3B82F6;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .table-container {
      overflow-x: auto;
    }

    .timeline-table {
      width: 100%;
    }

    .timeline-table th {
      background: #F9FAFB;
      color: #6B7280;
      font-weight: 600;
      font-size: 12px;
      padding: 10px 12px !important;
      border-bottom: 1px solid #E5E7EB;
    }

    .timeline-table td {
      padding: 8px 12px !important;
      font-size: 13px;
      border-bottom: 1px solid #F3F4F6;
    }

    .timeline-table tr {
      background: white;
    }

    .timeline-table tr:hover {
      background: #F9FAFB;
    }

    .type-badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 4px;
      text-transform: uppercase;
    }

    .type-badge.payment {
      background: #D1FAE5;
      color: #059669;
    }

    .type-badge.payable {
      background: #FEE2E2;
      color: #DC2626;
    }

    .ref-number {
      color: #6B7280;
      font-size: 12px;
    }

    .text-right {
      text-align: right !important;
    }

    .text-center {
      text-align: center !important;
    }

    .payment-amount {
      color: #059669;
      font-weight: 600;
      font-family: 'Roboto Mono', monospace;
    }

    .payable-amount {
      color: #DC2626;
      font-weight: 600;
      font-family: 'Roboto Mono', monospace;
    }

    .status-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: 600;
    }

    .status-badge.paid, .status-badge.allocated {
      background: #D1FAE5;
      color: #059669;
    }

    .status-badge.partial {
      background: #FEF3C7;
      color: #D97706;
    }

    .status-badge.unpaid {
      background: #FEE2E2;
      color: #DC2626;
    }

    .overdue-badge {
      display: inline-block;
      background: #DC2626;
      color: white;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      font-size: 10px;
      font-weight: 700;
      text-align: center;
      line-height: 16px;
      margin-left: 4px;
    }

    .actions-col {
      width: 110px;
      text-align: right !important;
      white-space: nowrap;
    }

    .actions-col button {
      display: inline-block !important;
      vertical-align: middle;
    }

    .small-btn {
      width: 32px !important;
      height: 32px !important;
      padding: 0 !important;
    }

    .small-btn .mat-icon {
      font-size: 18px !important;
      width: 18px !important;
      height: 18px !important;
      line-height: 18px !important;
    }

    .delete-btn:hover {
      color: #DC2626;
    }

    .info-section {
      margin-bottom: 24px;
    }

    .info-card {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .clickable-header {
      cursor: pointer;
      padding: 16px 20px !important;
    }

    .clickable-header:hover {
      background: #F9FAFB;
    }

    .info-card mat-card-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      color: #374151;
      margin: 0;
    }

    .expand-icon {
      margin-left: auto;
      color: #9CA3AF;
    }

    .section-icon {
      color: #3B82F6;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .info-card mat-card-content {
      padding: 0 20px 20px;
    }

    .info-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    }

    .info-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .info-label {
      font-size: 11px;
      color: #6B7280;
      text-transform: uppercase;
    }

    .info-value {
      font-size: 14px;
      color: #1F2937;
      font-weight: 500;
    }

    .bank-accounts {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #E5E7EB;
    }

    .bank-accounts h4 {
      margin: 0 0 12px 0;
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .bank-account {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px;
      background: #F9FAFB;
      border-radius: 6px;
      margin-bottom: 8px;
    }

    .account-number {
      font-family: 'Roboto Mono', monospace;
      font-weight: 500;
    }

    .primary-badge {
      background: #DBEAFE;
      color: #1D4ED8;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
    }

    .bank-name {
      color: #6B7280;
      font-size: 13px;
    }

    .empty-state, .error-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      color: #6B7280;
    }

    .empty-state mat-icon, .error-state mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      color: #D1D5DB;
      margin-bottom: 16px;
    }

    .empty-state p, .error-state p {
      margin: 0 0 16px 0;
      font-size: 16px;
    }

    .empty-actions {
      display: flex;
      gap: 12px;
    }
  `]
})
export class SupplierDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(SuppliersService);
  private payablesService = inject(PayablesService);
  private paymentsService = inject(PaymentsService);
  private snack = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  supplier: SupplierDetail | null = null;
  payments: Payment[] = [];
  payables: PayableDocument[] = [];
  timeline: TimelineItem[] = [];
  filteredTimeline: TimelineItem[] = [];
  loading = true;
  infoExpanded = false;
  totalPayments = 0;

  // Table columns
  timelineCols = ['type', 'number', 'date', 'amount', 'status', 'actions'];

  // Filters
  filterType: 'all' | 'payment' | 'payable' = 'all';
  filterFromDate: Date | null = null;
  filterToDate: Date | null = null;
  filterStatus: PaymentStatus | null = null;

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadData(id);
    } else {
      this.loading = false;
    }
  }

  loadData(id: string): void {
    this.loading = true;

    // Load supplier details
    this.service.getDetail(id).subscribe({
      next: (data) => {
        this.supplier = data;
        this.payables = data.invoices as PayableDocument[] || [];
        this.loadPayments(id);
      },
      error: () => {
        this.snack.open('Greška pri učitavanju podataka', 'Zatvori', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  loadPayments(supplierId: string): void {
    this.paymentsService.getBySupplierId(supplierId).subscribe({
      next: (payments) => {
        this.payments = payments;
        this.totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
        this.buildTimeline();
        this.loading = false;
      },
      error: () => {
        this.buildTimeline();
        this.loading = false;
      }
    });
  }

  buildTimeline(): void {
    this.timeline = [];

    // Add payables
    this.payables.forEach(p => {
      const date = p.invoiceDate ? new Date(p.invoiceDate) : new Date(p.createdAtUtc || Date.now());
      this.timeline.push({
        type: 'payable',
        date,
        data: p
      });
    });

    // Add payments
    this.payments.forEach(p => {
      this.timeline.push({
        type: 'payment',
        date: new Date(p.paymentDate),
        data: p
      });
    });

    // Sort by date descending (newest first)
    this.timeline.sort((a, b) => b.date.getTime() - a.date.getTime());

    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredTimeline = this.timeline.filter(item => {
      // Type filter
      if (this.filterType !== 'all' && item.type !== this.filterType) {
        return false;
      }

      // Date filters
      if (this.filterFromDate && item.date < this.filterFromDate) {
        return false;
      }
      if (this.filterToDate) {
        const endOfDay = new Date(this.filterToDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (item.date > endOfDay) {
          return false;
        }
      }

      // Status filter (only for payables)
      if (this.filterStatus !== null && item.type === 'payable') {
        const payable = item.data as PayableDocument;
        // Calculate actual status from amounts
        const actualStatus = this.getPayableNumericStatus(payable);
        if (actualStatus !== this.filterStatus) {
          return false;
        }
      }

      return true;
    });
  }

  hasFilters(): boolean {
    return this.filterType !== 'all' || !!this.filterFromDate || !!this.filterToDate || this.filterStatus !== null;
  }

  clearFilters(): void {
    this.filterType = 'all';
    this.filterFromDate = null;
    this.filterToDate = null;
    this.filterStatus = null;
    this.applyFilters();
  }

  asPayment(data: Payment | PayableDocument): Payment {
    return data as Payment;
  }

  asPayable(data: Payment | PayableDocument): PayableDocument {
    return data as PayableDocument;
  }

  getDocumentTypeLabel(type: DocumentType | undefined | null): string {
    const labels: Record<number, string> = {
      0: 'FAKTURA',
      1: 'GOTOVINSKI',
      2: 'AVANSNI',
      3: 'PROFAKTURA',
      4: 'ODOBRENJE',
      5: 'ZADUŽENJE'
    };
    // Default to FAKTURA (0) if type is undefined/null
    return labels[type ?? 0] ?? 'FAKTURA';
  }

  getStatusClass(status: PaymentStatus): string {
    if (status === PaymentStatus.Paid) return 'paid';
    if (status === PaymentStatus.PartiallyPaid) return 'partial';
    return 'unpaid';
  }

  getStatusLabel(status: PaymentStatus): string {
    if (status === PaymentStatus.Paid) return 'Plaćeno';
    if (status === PaymentStatus.PartiallyPaid) return 'Delimično';
    return 'Neplaćeno';
  }

  // Status based on actual amounts, not server status field
  getPayableStatusClass(payable: PayableDocument): string {
    if (payable.paidAmount >= payable.amount) return 'paid';
    if (payable.paidAmount > 0) return 'partial';
    return 'unpaid';
  }

  // Numeric status for filtering: 0=Unpaid, 1=PartiallyPaid, 2=Paid
  getPayableNumericStatus(payable: PayableDocument): number {
    if (payable.paidAmount >= payable.amount) return 2; // Paid
    if (payable.paidAmount > 0) return 1; // PartiallyPaid
    return 0; // Unpaid
  }

  getPayableStatusLabel(payable: PayableDocument): string {
    if (payable.paidAmount >= payable.amount) return 'Plaćeno';
    if (payable.paidAmount > 0) return 'Delimično';
    return 'Neplaćeno';
  }

  goBack(): void {
    this.router.navigate(['/suppliers']);
  }

  hasBasicInfo(): boolean {
    if (!this.supplier) return false;
    return !!(
      this.supplier.address ||
      this.supplier.city ||
      this.supplier.taxId ||
      this.supplier.registrationNumber ||
      this.supplier.phone ||
      this.supplier.email ||
      this.supplier.contactPerson ||
      this.supplier.bankAccounts?.length
    );
  }

  openAddPayable(): void {
    if (!this.supplier) return;
    const ref = this.dialog.open(SupplierPayableDialogComponent, {
      data: { supplierId: this.supplier.id, supplierName: this.supplier.name },
      width: '600px'
    });
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.payablesService.create(result).subscribe({
          next: () => {
            this.snack.open('Dugovanje je dodato', 'Zatvori', { duration: 2000 });
            this.loadData(this.supplier!.id);
          },
          error: (e) => this.snack.open(e?.error?.message || 'Greška', 'Zatvori', { duration: 3000 })
        });
      }
    });
  }

  openAddPayment(): void {
    if (!this.supplier) return;
    const unpaidDocs = this.payables.filter(p => p.remainingAmount > 0);
    const ref = this.dialog.open(SupplierPaymentDialogComponent, {
      data: {
        supplierId: this.supplier.id,
        supplierName: this.supplier.name,
        unpaidDocuments: unpaidDocs
      },
      width: '650px'
    });
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.paymentsService.create(result).subscribe({
          next: () => {
            this.snack.open('Uplata je kreirana', 'Zatvori', { duration: 2000 });
            this.loadData(this.supplier!.id);
          },
          error: (e) => this.snack.open(e?.error?.message || 'Greška', 'Zatvori', { duration: 3000 })
        });
      }
    });
  }

  editPayable(payable: PayableDocument): void {
    if (!this.supplier) return;
    const ref = this.dialog.open(SupplierPayableDialogComponent, {
      data: {
        supplierId: this.supplier.id,
        supplierName: this.supplier.name,
        payable
      },
      width: '600px'
    });
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.payablesService.update(payable.id, result).subscribe({
          next: () => {
            this.snack.open('Dugovanje je ažurirano', 'Zatvori', { duration: 2000 });
            this.loadData(this.supplier!.id);
          },
          error: (e) => this.snack.open(e?.error?.message || 'Greška', 'Zatvori', { duration: 3000 })
        });
      }
    });
  }

  deleteItem(item: TimelineItem): void {
    const message = item.type === 'payment'
      ? 'Da li ste sigurni da želite da obrišete ovu uplatu?'
      : 'Da li ste sigurni da želite da obrišete ovo dugovanje?';

    if (!confirm(message)) return;

    if (item.type === 'payment') {
      this.paymentsService.delete((item.data as Payment).id).subscribe({
        next: () => {
          this.snack.open('Uplata je obrisana', 'Zatvori', { duration: 2000 });
          this.loadData(this.supplier!.id);
        },
        error: (e) => this.snack.open(e?.error?.message || 'Greška', 'Zatvori', { duration: 3000 })
      });
    } else {
      this.payablesService.delete((item.data as PayableDocument).id).subscribe({
        next: () => {
          this.snack.open('Dugovanje je obrisano', 'Zatvori', { duration: 2000 });
          this.loadData(this.supplier!.id);
        },
        error: (e) => this.snack.open(e?.error?.message || 'Greška', 'Zatvori', { duration: 3000 })
      });
    }
  }

  viewPayablePayments(payable: PayableDocument): void {
    this.dialog.open(PaymentDetailsDialogComponent, {
      data: { payable },
      width: '600px'
    });
  }
}

// Dialog for adding/editing payable
@Component({
  selector: 'app-supplier-payable-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title>{{ data.payable ? 'Izmeni dugovanje' : 'Novo dugovanje' }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()">
      <mat-dialog-content>
        <div class="supplier-name">{{ data.supplierName }}</div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Tip dokumenta</mat-label>
          <mat-select formControlName="documentType" required>
            <mat-option [value]="0">Faktura</mat-option>
            <mat-option [value]="1">Gotovinski račun</mat-option>
            <mat-option [value]="2">Avansni račun</mat-option>
            <mat-option [value]="3">Profaktura</mat-option>
            <mat-option [value]="4">Knjižno odobrenje</mat-option>
            <mat-option [value]="5">Knjižno zaduženje</mat-option>
          </mat-select>
        </mat-form-field>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Broj računa</mat-label>
            <input matInput formControlName="invoiceNumber">
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Broj otpremnice</mat-label>
            <input matInput formControlName="deliveryNoteNumber">
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Datum računa</mat-label>
            <input matInput [matDatepicker]="invPicker" formControlName="invoiceDate">
            <mat-datepicker-toggle matSuffix [for]="invPicker"></mat-datepicker-toggle>
            <mat-datepicker #invPicker></mat-datepicker>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Datum valute</mat-label>
            <input matInput [matDatepicker]="duePicker" formControlName="dueDate">
            <mat-datepicker-toggle matSuffix [for]="duePicker"></mat-datepicker-toggle>
            <mat-datepicker #duePicker></mat-datepicker>
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Iznos</mat-label>
            <input matInput type="number" step="0.01" formControlName="amount" required>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Plaćeno</mat-label>
            <input matInput type="number" step="0.01" formControlName="paidAmount">
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Poziv na broj</mat-label>
          <input matInput formControlName="referenceNumber">
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Napomena</mat-label>
          <textarea matInput formControlName="note" rows="2"></textarea>
        </mat-form-field>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button mat-dialog-close type="button">Otkaži</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="form.invalid">
          {{ data.payable ? 'Sačuvaj' : 'Dodaj' }}
        </button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [`
    mat-dialog-content {
      min-width: 500px;
    }
    .supplier-name {
      font-weight: 600;
      color: #374151;
      margin-bottom: 16px;
      padding: 8px 12px;
      background: #F3F4F6;
      border-radius: 6px;
    }
    .full-width {
      width: 100%;
    }
    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .form-row mat-form-field {
      width: 100%;
    }
  `]
})
export class SupplierPayableDialogComponent {
  private fb = inject(FormBuilder);

  form = this.fb.group({
    documentType: [0, Validators.required],
    invoiceNumber: [''],
    deliveryNoteNumber: [''],
    invoiceDate: [null as Date | null],
    dueDate: [null as Date | null],
    amount: [0, [Validators.required, Validators.min(0.01)]],
    paidAmount: [0],
    referenceNumber: [''],
    note: [''],
    paymentCurrency: ['RSD']
  });

  constructor(
    private dialogRef: MatDialogRef<SupplierPayableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      supplierId: string;
      supplierName: string;
      payable?: PayableDocument;
    }
  ) {
    if (data.payable) {
      this.form.patchValue({
        documentType: data.payable.documentType,
        invoiceNumber: data.payable.invoiceNumber || '',
        deliveryNoteNumber: data.payable.deliveryNoteNumber || '',
        invoiceDate: data.payable.invoiceDate ? new Date(data.payable.invoiceDate) : null,
        dueDate: data.payable.dueDate ? new Date(data.payable.dueDate) : null,
        amount: data.payable.amount,
        paidAmount: data.payable.paidAmount,
        referenceNumber: data.payable.referenceNumber || '',
        note: data.payable.note || '',
        paymentCurrency: data.payable.paymentCurrency || 'RSD'
      });
    }
  }

  save(): void {
    if (this.form.invalid) return;
    const v = this.form.value;
    const dto: CreatePayableDto = {
      supplierId: this.data.supplierId,
      documentType: v.documentType!,
      invoiceNumber: v.invoiceNumber || null,
      deliveryNoteNumber: v.deliveryNoteNumber || null,
      invoiceDate: v.invoiceDate ? v.invoiceDate.toISOString() : null,
      dueDate: v.dueDate ? v.dueDate.toISOString() : null,
      amount: v.amount!,
      paidAmount: v.paidAmount || 0,
      referenceNumber: v.referenceNumber || null,
      note: v.note || null,
      paymentCurrency: v.paymentCurrency || 'RSD'
    };
    this.dialogRef.close(dto);
  }
}

// Dialog for adding payment
interface DocSelection {
  document: PayableDocument;
  selected: boolean;
  amount: number;
  payFull: boolean;
}

@Component({
  selector: 'app-supplier-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    DecimalPipe,
    DatePipe
  ],
  template: `
    <h2 mat-dialog-title>Nova uplata</h2>
    <mat-dialog-content>
      <div class="supplier-name">{{ data.supplierName }}</div>

      <div class="form-row">
        <mat-form-field appearance="outline">
          <mat-label>Iznos uplate</mat-label>
          <input matInput type="number" [(ngModel)]="amount" required min="0.01" step="0.01">
          <span matSuffix>RSD</span>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Datum uplate</mat-label>
          <input matInput [matDatepicker]="picker" [(ngModel)]="paymentDate" required>
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>
      </div>

      <div class="form-row">
        <mat-form-field appearance="outline">
          <mat-label>Poziv na broj</mat-label>
          <input matInput [(ngModel)]="referenceNumber">
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Valuta</mat-label>
          <mat-select [(ngModel)]="currency">
            <mat-option value="RSD">RSD</mat-option>
            <mat-option value="EUR">EUR</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Napomena</mat-label>
        <textarea matInput [(ngModel)]="note" rows="2"></textarea>
      </mat-form-field>

      <div class="allocation-toggle">
        <mat-slide-toggle [(ngModel)]="autoAllocate">
          Automatsko raspoređivanje (FIFO)
        </mat-slide-toggle>
        <span class="hint">{{ autoAllocate ? 'Uplata će biti automatski raspoređena na najstarija neplaćena dugovanja' : 'Ručno izaberite dugovanja' }}</span>
      </div>

      <div class="manual-section" *ngIf="!autoAllocate && docSelections.length > 0">
        <h4>Neplaćena dugovanja ({{ docSelections.length }})</h4>
        <div class="doc-list">
          <div class="doc-item" *ngFor="let item of docSelections" [class.selected]="item.selected">
            <mat-checkbox [(ngModel)]="item.selected" (change)="onSelectionChange(item)"></mat-checkbox>
            <div class="doc-info">
              <span class="doc-number">{{ item.document.invoiceNumber || 'Bez broja' }}</span>
              <span class="doc-remaining">Preostalo: {{ item.document.remainingAmount | number:'1.2-2' }}</span>
            </div>
            <div class="doc-amount" *ngIf="item.selected">
              <mat-checkbox [(ngModel)]="item.payFull" (change)="onPayFullChange(item)">Ceo iznos</mat-checkbox>
              <mat-form-field appearance="outline" class="amount-input" *ngIf="!item.payFull">
                <input matInput type="number" [(ngModel)]="item.amount" min="0.01" [max]="item.document.remainingAmount" step="0.01">
              </mat-form-field>
            </div>
          </div>
        </div>
        <div class="allocation-summary" *ngIf="getTotalAllocated() > 0">
          <div class="summary-row">
            <span>Iznos uplate:</span>
            <span>{{ amount | number:'1.2-2' }} RSD</span>
          </div>
          <div class="summary-row">
            <span>Raspoređeno:</span>
            <span [class.over]="getTotalAllocated() > amount">{{ getTotalAllocated() | number:'1.2-2' }} RSD</span>
          </div>
        </div>
      </div>

      <div class="no-docs" *ngIf="!autoAllocate && docSelections.length === 0">
        <mat-icon>check_circle</mat-icon>
        <span>Nema neplaćenih dugovanja</span>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Otkaži</button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="!isValid()">
        Sačuvaj
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content {
      min-width: 550px;
    }
    .supplier-name {
      font-weight: 600;
      color: #374151;
      margin-bottom: 16px;
      padding: 8px 12px;
      background: #F3F4F6;
      border-radius: 6px;
    }
    .full-width {
      width: 100%;
    }
    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    .form-row mat-form-field {
      width: 100%;
    }
    .allocation-toggle {
      margin: 16px 0;
      padding: 12px;
      background: #F9FAFB;
      border-radius: 8px;
    }
    .allocation-toggle .hint {
      display: block;
      margin-top: 4px;
      font-size: 12px;
      color: #6B7280;
    }
    .manual-section h4 {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 600;
    }
    .doc-list {
      max-height: 250px;
      overflow-y: auto;
    }
    .doc-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px;
      background: white;
      border: 1px solid #E5E7EB;
      border-radius: 6px;
      margin-bottom: 8px;
    }
    .doc-item.selected {
      background: #EFF6FF;
      border-color: #3B82F6;
    }
    .doc-info {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .doc-number {
      font-weight: 500;
    }
    .doc-remaining {
      font-size: 12px;
      color: #DC2626;
    }
    .doc-amount {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .amount-input {
      width: 100px;
    }
    .amount-input ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      display: none;
    }
    .allocation-summary {
      margin-top: 12px;
      padding: 12px;
      background: white;
      border-radius: 6px;
      border: 1px solid #E5E7EB;
    }
    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 13px;
    }
    .summary-row span:last-child {
      font-weight: 600;
      font-family: 'Roboto Mono', monospace;
    }
    .summary-row .over {
      color: #DC2626;
    }
    .no-docs {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 20px;
      justify-content: center;
      color: #10B981;
    }
  `]
})
export class SupplierPaymentDialogComponent {
  amount = 0;
  paymentDate: Date = new Date();
  referenceNumber = '';
  currency = 'RSD';
  note = '';
  autoAllocate = true;
  docSelections: DocSelection[] = [];

  constructor(
    private dialogRef: MatDialogRef<SupplierPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      supplierId: string;
      supplierName: string;
      unpaidDocuments: PayableDocument[];
    }
  ) {
    this.docSelections = data.unpaidDocuments.map(doc => ({
      document: doc,
      selected: false,
      amount: doc.remainingAmount,
      payFull: true
    }));
  }

  onSelectionChange(item: DocSelection): void {
    if (item.selected && item.payFull) {
      item.amount = item.document.remainingAmount;
    }
  }

  onPayFullChange(item: DocSelection): void {
    if (item.payFull) {
      item.amount = item.document.remainingAmount;
    }
  }

  getTotalAllocated(): number {
    return this.docSelections.filter(d => d.selected).reduce((sum, d) => sum + (d.amount || 0), 0);
  }

  isValid(): boolean {
    if (!this.amount || this.amount <= 0 || !this.paymentDate) return false;
    if (!this.autoAllocate && this.getTotalAllocated() > this.amount) return false;
    return true;
  }

  save(): void {
    if (!this.isValid()) return;

    const manualAllocations: ManualAllocation[] = this.autoAllocate ? [] :
      this.docSelections.filter(d => d.selected && d.amount > 0).map(d => ({
        payableDocumentId: d.document.id,
        amount: d.amount
      }));

    const dto: CreatePaymentDto = {
      supplierId: this.data.supplierId,
      amount: this.amount,
      paymentDate: this.paymentDate.toISOString(),
      referenceNumber: this.referenceNumber || undefined,
      note: this.note || undefined,
      paymentCurrency: this.currency,
      autoAllocate: this.autoAllocate,
      manualAllocations
    };

    this.dialogRef.close(dto);
  }
}
